import { t } from "i18next";

import type { ColumnsClarFactory } from "@/core/components/shared/data-table";
import { Badge } from "@/core/components/ui/badge";
import { NoTranslate } from "@/core/components/ui/no-translate";
import type { CompanySubscriptionDto } from "@/features/fee-engine/api/dto";
import { CompanySubscriptionRowActions } from "@/features/fee-engine/components/company-subscriptions/company-subscription-row-actions";
import {
  SUBSCRIPTION_STATUSES,
  type SubscriptionStatus,
} from "@/features/shared/api/enums";

export type CompanySubscriptionRow = CompanySubscriptionDto & { id: string };

const selectOptions = (values: readonly string[]) =>
  values.map((value) => ({ value, label: value }));

const STATUS_VARIANTS: Record<
  SubscriptionStatus,
  "success" | "destructive" | "warning"
> = {
  ACTIVE: "success",
  CANCELLED: "destructive",
  PAST_DUE: "warning",
};

/**
 * Colonnes de la liste des abonnements entreprise
 * (`POST /CompanySubscriptions/all`).
 */
export const companySubscriptionsColumns: ColumnsClarFactory<CompanySubscriptionRow> = (
  navigate
) => [
  {
    id: "companyPartyId",
    accessorKey: "companyPartyId",
    header: t("fees.subscriptions.columns.companyPartyId"),
    cell: ({ getValue }) => (
      <NoTranslate as="span" className="font-mono text-xs">
        {(getValue() as string) || "—"}
      </NoTranslate>
    ),
    meta: { type: "text" },
  },
  {
    id: "planId",
    accessorKey: "planId",
    header: t("fees.subscriptions.columns.planId"),
    cell: ({ getValue }) => (
      <NoTranslate as="span" className="font-mono text-xs">
        {(getValue() as string) || "—"}
      </NoTranslate>
    ),
    meta: { type: "denied" },
  },
  {
    id: "status",
    accessorKey: "status",
    header: t("fees.subscriptions.columns.status"),
    cell: ({ getValue }) => {
      const status = getValue() as SubscriptionStatus | undefined;
      return status ? (
        <NoTranslate as="span">
          <Badge variant={STATUS_VARIANTS[status] ?? "outline"}>{status}</Badge>
        </NoTranslate>
      ) : (
        "—"
      );
    },
    meta: { type: "select", options: selectOptions(SUBSCRIPTION_STATUSES) },
  },
  {
    id: "startDate",
    accessorKey: "startDate",
    header: t("fees.subscriptions.columns.startDate"),
    cell: ({ getValue }) => (
      <NoTranslate as="span" className="text-xs">
        {(getValue() as string) || "—"}
      </NoTranslate>
    ),
    meta: { type: "denied" },
  },
  {
    id: "nextBillingDate",
    accessorKey: "nextBillingDate",
    header: t("fees.subscriptions.columns.nextBillingDate"),
    cell: ({ getValue }) => (
      <NoTranslate as="span" className="text-xs">
        {(getValue() as string) || "—"}
      </NoTranslate>
    ),
    meta: { type: "denied" },
  },
  {
    id: "actions",
    header: t("common.actions"),
    enableHiding: false,
    cell: ({ row }) =>
      row.original.id ? (
        <CompanySubscriptionRowActions
          companySubscription={row.original}
          navigate={navigate}
        />
      ) : null,
    meta: { type: "denied" },
  },
];
